if (!customElements.get('theme-dropdown')) {
  class ThemeDropdown extends HTMLElement {
    constructor() {
      super();
      this.trigger = null;
      this.panel = null;
      this.typeaheadBuffer = '';
      this.typeaheadTimer = null;
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
      this.handlePanelKeydown = this.handlePanelKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.requestReposition = this.requestReposition.bind(this);
      this.positionAbortController = null;
      this.repositionFrame = null;
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-dropdown-trigger]');
      this.panel = this.querySelector('[data-dropdown-panel]');

      if (!this.trigger || !this.panel) return;

      const mode = this.dataset.mode || 'listbox';
      this.trigger.setAttribute('aria-haspopup', mode);
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('popover', 'manual');

      this.trigger.addEventListener('click', this.handleTriggerClick);
      this.trigger.addEventListener('keydown', this.handleTriggerKeydown);
      this.panel.addEventListener('keydown', this.handlePanelKeydown);
      document.addEventListener('click', this.handleDocumentClick);
      this.addEventListener('focusout', this.handleFocusOut);
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.handleTriggerClick);
      this.trigger?.removeEventListener('keydown', this.handleTriggerKeydown);
      this.panel?.removeEventListener('keydown', this.handlePanelKeydown);
      document.removeEventListener('click', this.handleDocumentClick);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.positionAbortController?.abort();
      clearTimeout(this.typeaheadTimer);
      cancelAnimationFrame(this.repositionFrame);
    }

    get isOpen() {
      return this.panel?.matches(':popover-open') ?? false;
    }

    get items() {
      return [...(this.panel?.querySelectorAll('[role="option"], [role="menuitem"]') ?? [])];
    }

    open() {
      if (this.isOpen) return;
      this.panel.removeAttribute('hidden');
      this.panel.showPopover();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.updatePlacement();

      this.positionAbortController = new AbortController();
      const { signal } = this.positionAbortController;
      window.addEventListener('scroll', this.requestReposition, { capture: true, passive: true, signal });
      window.addEventListener('resize', this.requestReposition, { signal });

      this.publishEvent('open');
    }

    close() {
      if (!this.isOpen) return;
      this.panel.hidePopover();
      this.panel.setAttribute('hidden', '');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.removeAttribute('data-placement');
      this.positionAbortController?.abort();
      this.positionAbortController = null;
      this.publishEvent('close');
    }

    updatePlacement() {
      const triggerRect = this.trigger.getBoundingClientRect();
      this.panel.style.minWidth = `${triggerRect.width}px`;

      const panelWidth = this.panel.offsetWidth;
      const maxLeft = window.innerWidth - panelWidth;
      const left = Math.max(0, Math.min(triggerRect.left, maxLeft));
      this.panel.style.left = `${left}px`;

      const ctaHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-cta-height')) || 0;
      const panelHeight = this.panel.offsetHeight;
      const spaceBelow = (window.innerHeight - ctaHeight) - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const placeTop = spaceBelow < panelHeight && spaceAbove > spaceBelow;
      this.panel.style.top = placeTop ? `${triggerRect.top - panelHeight}px` : `${triggerRect.bottom}px`;
      this.setAttribute('data-placement', placeTop ? 'top' : 'bottom');
    }

    requestReposition() {
      if (this.repositionFrame) return;
      this.repositionFrame = requestAnimationFrame(() => {
        this.repositionFrame = null;
        if (this.isOpen) this.updatePlacement();
      });
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    handleTriggerClick(event) {
      event.stopPropagation();
      this.toggle();
    }

    handleTriggerKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.open();
        requestAnimationFrame(() => this.items[0]?.focus());
      } else if (event.key === 'Escape') {
        this.close();
      }
    }

    handlePanelKeydown(event) {
      const items = this.items;
      if (items.length === 0) return;

      const index = items.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          items[(index + 1) % items.length].focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          items[(index - 1 + items.length) % items.length].focus();
          break;
        case 'Home':
          event.preventDefault();
          items[0].focus();
          break;
        case 'End':
          event.preventDefault();
          items[items.length - 1].focus();
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          this.trigger.focus();
          break;
        default:
          if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
            this.handleTypeahead(event.key, items);
          }
      }
    }

    handleTypeahead(char, items) {
      this.typeaheadBuffer += char.toLowerCase();

      clearTimeout(this.typeaheadTimer);
      this.typeaheadTimer = setTimeout(() => {
        this.typeaheadBuffer = '';
      }, 500);

      const singleChar = this.typeaheadBuffer.length === 1;
      const matches = items.filter(item =>
        item.textContent.trim().toLowerCase().startsWith(this.typeaheadBuffer)
      );

      if (matches.length === 0) return;

      if (singleChar) {
        const currentIndex = matches.indexOf(document.activeElement);
        const next = matches[(currentIndex + 1) % matches.length];
        next?.focus();
      } else {
        matches[0]?.focus();
      }
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.close();
      }
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) {
        this.close();
      }
    }

    publishEvent(action) {
      const prefix = this.dataset.event;
      if (!prefix || typeof theme === 'undefined' || !theme.pubsub) return;
      theme.pubsub.publish(`${prefix}:${action}`, { element: this });
    }
  }

  customElements.define('theme-dropdown', ThemeDropdown);
}
